import { useState, useEffect, useCallback } from 'react';

export interface SpotlightRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Hook that tracks a DOM element's position by CSS selector.
 * Returns the bounding rect (updated on scroll/resize) or null if not found.
 */
export function useSpotlightPosition(selector: string | null): {
  rect: SpotlightRect | null;
  isVisible: boolean;
} {
  const [rect, setRect] = useState<SpotlightRect | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const measure = useCallback(() => {
    if (!selector) {
      setRect(null);
      setIsVisible(false);
      return;
    }

    const el = document.querySelector<HTMLElement>(selector);
    if (!el) {
      setRect(null);
      setIsVisible(false);
      return;
    }

    const domRect = el.getBoundingClientRect();
    const visible =
      domRect.width > 0 &&
      domRect.height > 0 &&
      domRect.bottom > 0 &&
      domRect.top < window.innerHeight;

    setRect({
      x: domRect.x,
      y: domRect.y,
      width: domRect.width,
      height: domRect.height,
    });
    setIsVisible(visible);
  }, [selector]);

  useEffect(() => {
    if (!selector) {
      setRect(null);
      setIsVisible(false);
      return;
    }

    // Initial measurement
    measure();

    // Observe resize on the target
    const el = document.querySelector<HTMLElement>(selector);
    let resizeObserver: ResizeObserver | null = null;
    if (el) {
      resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(measure);
      });
      resizeObserver.observe(el);
    }

    // Listen for scroll & resize
    const handleUpdate = () => {
      requestAnimationFrame(measure);
    };
    window.addEventListener('scroll', handleUpdate, { passive: true });
    window.addEventListener('resize', handleUpdate, { passive: true });

    // Also listen for scroll on the main content area (overflow scroll)
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.addEventListener('scroll', handleUpdate, { passive: true });
    }

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('scroll', handleUpdate);
      window.removeEventListener('resize', handleUpdate);
      if (mainContent) {
        mainContent.removeEventListener('scroll', handleUpdate);
      }
    };
  }, [selector, measure]);

  return { rect, isVisible };
}
